import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UserSidebar from './user/UserSidebar';
import UserDashboard from './user/UserDashboard';
import UserProducts from './user/UserProducts';
import UserCart from './user/UserCart';
import UserIncome from './user/UserIncome';
import UserPurchases from './user/UserPurchases';
import UserRanks from './user/UserRanks';
import UserPayouts from './user/UserPayouts';
import UserDownline from './user/UserDownline';
import UserSettings from './user/UserSettings';
import UserKYC from './user/UserKYC';
import UserSendMoney from './user/UserSendMoney';
import UserResetPassword from './user/UserResetPassword';
import UserFundTransfer from './user/UserFundTransfer';
import UserFundTransferHistory from './user/UserFundTransferHistory';
import RealTimeBonanzaProgressTracker from './user/RealTimeBonanzaProgressTracker';
import Team15Levels from './user/Team15Levels';
import UserMainToDepositTransfer from './user/UserMainToDepositTransfer';
import UserInvestmentPlans from './UserInvestmentPlans';
import UserLifetimeRewards from './user/UserLifetimeRewards';

const UserLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const tabByPath = {
      '/dashboard': 'dashboard',
      '/user': 'dashboard',
      '/user/products': 'products',
      '/user/cart': 'cart',
      '/user/purchases': 'purchases',
      '/user/downline': 'downline',
      '/user/team-15': 'team-15',
      '/user/ranks': 'ranks',
      '/user/income': 'income',
      '/user/payouts': 'payouts',
      '/user/settings': 'settings',
      '/user/kyc': 'kyc',
      '/user/send-money': 'send-money',
      '/user/fund-transfer': 'fund-transfer',
      '/user/fund-transfer-history': 'fund-transfer-history',
      '/user/main-to-deposit': 'main-to-deposit',
      '/user/bonanza': 'bonanza',
      '/user/reset-password': 'reset-password',
      '/user/investment-plans': 'investment-plans',
      '/user/lifetime-rewards': 'lifetime-rewards'
    };

    if (tabByPath[location.pathname]) {
      setActiveTab(tabByPath[location.pathname]);
    }
  }, [location.pathname]);


  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <UserDashboard />;
      case 'products':
        return <UserProducts />;
      case 'cart':
        return <UserCart />;
      case 'purchases':
        return <UserPurchases />;
      case 'downline':
        return <UserDownline />;
      case 'team-15':
        return <Team15Levels />;
      case 'income':
        return <UserIncome />;
      case 'ranks':
        return <UserRanks />;
      case 'payouts':
        return <UserPayouts />;
      case 'settings':
        return <UserSettings />;
      case 'kyc':
        return <UserKYC />;
      case 'send-money':
        return <UserSendMoney />;
      case 'fund-transfer':
        return <UserFundTransfer />;
      case 'fund-transfer-history':
        return <UserFundTransferHistory />;
      case 'main-to-deposit':
        return <UserMainToDepositTransfer />;
      case 'bonanza':
        return <RealTimeBonanzaProgressTracker />;
      case 'reset-password':
        return <UserResetPassword />;
      case 'investment-plans':
        return <UserInvestmentPlans />;
      case 'lifetime-rewards':
        return <UserLifetimeRewards />;
      default:
        return <UserDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex overflow-hidden relative">
      {/* Sidebar - Fixed position on desktop, slide-out on mobile */}
      <UserSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      {/* Main Section - Independent scrolling */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden lg:ml-0">
        {/* Top Header - Fixed */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <div>
                <h1 className="text-lg sm:text-xl font-bold text-[#0F2F3D] capitalize">
                  {activeTab}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {user?.name} • {user?.mySponsorCode}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                {user?.name}
              </div>

              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white flex items-center justify-center font-bold text-sm sm:text-base">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div
                onClick={() => {
                  const referralLink = `https://www.desimart.net/register?sponserid=${user?.mySponsorCode}`;
                  navigator.clipboard.writeText(referralLink);
                  alert("Referral link copied!");
                }}
                className="group relative w-auto px-6 py-3 rounded-full bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white font-semibold text-sm sm:text-base hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 border border-white/20"
              >
                <svg
                  className="w-4 h-4 transition-transform group-hover:rotate-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>Copy link</span>

                {/* Optional: Shine effect */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
            </div>
          </div>
        </div>

        {/* Page Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 py-4 sm:py-6">
            <div className="max-w-7xl mx-auto">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLayout;
